import { request, requestWithOptionalAuth } from "./request";
import type { Review } from "@/types/review";
import type { FeedResponse, FeedReview } from "./dashboardService";

export type Category = {
  id: string;
  title: {
    ar: string;
    en: string;
  };
  description: string;
  slug: string;
  storeCount: number;
  reviewCount: number;
  topStores: Array<{
    id: string;
    name: string;
    logoUrl?: string;
  }>;
};

// Mirrors the backend CategoryInsights payload (categoryInsightsService.ts). Every review number
// uses the home-feed eligibility allowlist; nothing here is an editorial ranking.
export type CategoryInsightReview = Review & {
  store: { _id: string; name: string; slug?: string; logoUrl?: string };
};

export type CategoryInsights = {
  slug: string;
  storeCount: number;
  storeReviewTotal: number;
  reviewCount: number;
  avgRating: number | null;
  lowRatingShare: number | null;
  reviewedStoreCount: number;
  recentReviews: CategoryInsightReview[];
  mostReviewedStores: Array<{
    _id: string;
    name: string;
    slug: string;
    logoUrl?: string;
    reviewCount: number;
    avgRating: number;
  }>;
  relatedCategories: Array<{ slug: string; titleAr: string; sharedStores: number }>;
  noindex: boolean;
  computedAt: string;
};

export const categoryService = {
  getCategories: async () =>
    request<Category[]>({
      path: "/categories",
      method: "GET",
    }),

  getCategoryById: async (id: string) => {
    const response = await request<Category>({
      path: `/categories/id/${id}`,
      method: "GET",
    });
    return response;
  },

  getCategoryBySlug: async (slug: string) => {
    const response = await request<Category>({
      path: `/categories/slug/${slug}`,
      method: "GET",
    });
    return response;
  },

  getCategoryInsights: async (slug: string) =>
    request<CategoryInsights>({
      path: `/categories/slug/${slug}/insights`,
      method: "GET",
    }),

  // The category page's experiences feed (quick 261010-oyg): the home feed's cursor contract,
  // scoped to one category. Public; the token rides along when present so likes and personal
  // exclusions apply.
  getCategoryFeed: async (
    slug: string,
    { cursor, limit = 10 }: { cursor?: string; limit?: number } = {}
  ) =>
    requestWithOptionalAuth<CategoryFeedPage>({
      path: `/categories/slug/${slug}/feed`,
      method: "GET",
      params: { ...(cursor ? { cursor } : {}), limit },
    }),

  // Semantic search over the category's reviews, already hydrated to feed cards, rank order.
  searchCategoryReviews: async (slug: string, q: string, limit = 20) =>
    requestWithOptionalAuth<CategoryReviewSearchResult>({
      path: `/categories/slug/${slug}/search`,
      method: "GET",
      params: { q, limit },
    }),
};

export type CategoryFeedPage = FeedResponse;
export type CategoryReviewSearchResult = { reviews: FeedReview[]; totalHits: number };
